import type { ICustomPermalinkResolver } from '../media/ICustomPermalinkResolver';
import { BundledSettingsProvider } from '../configuration/bundledSettingsProvider';
import { AspectRatio, type AspectRatioMap, emptyAspectRatioMap, imQuery, type TransformOptions } from '../utils/imageTransform';
import type { DamMediaContract } from '../media/damMediaContract';
import type { IMediaDownloadUrlResolver } from '../media/IMediaDownloadUrlResolver';

const mediaPath: string = 'uploads/';

export class DiscoveryDAMMediaPermalinkResolverRuntime implements ICustomPermalinkResolver {

    private _dynamicDomain: (external?: string) => string;
    private _staticDomain: (external?: string) => string;

    public constructor(
        private readonly mediaDownloadUrlResolver: IMediaDownloadUrlResolver
    ) {}

    public dynamicDomain(external?: string): string {
        if (!this._dynamicDomain) {
            this._dynamicDomain = (ext?: string) => BundledSettingsProvider.newInstance().getSettingSync<string>(`dam${ext ? `/${ext}` : ''}/dynamicDomain`);
        }
        return this._dynamicDomain(external);
    }

    public staticDomain(external?: string): string {
        if (!this._staticDomain) {
            this._staticDomain = (ext?: string) => BundledSettingsProvider.newInstance().getSettingSync<string>(`dam${ext ? `/${ext}` : ''}/staticDomain`);
        }
        return this._staticDomain(external);
    }

    public canHandleTarget(targetKey: string): boolean {
        return targetKey.startsWith(mediaPath);
    }

    public async getUrlFromMediaContract(contract: DamMediaContract, options?: TransformOptions): Promise<string> {
        if (!contract) {
            throw new Error('Media contract cannot be neither empty nor null.');
        }
        if (!contract.dam) {
            return contract.permalink;
        } else if (contract.mimeType?.startsWith('image/') && contract.mimeType !== 'image/svg+xml') {
            return `https://${this.dynamicDomain(contract.external)}${contract.permalink}${imQuery(contract, options)}`;
        } else {
            return this.mediaDownloadUrlResolver.resolve(contract);
        }
    }

    public async getAspectRatioUrlsByMediaContract(contract: DamMediaContract): Promise<AspectRatioMap> {
        if (!contract) {
            throw new Error('Media contract cannot be neither empty nor null.');
        }
        const map: AspectRatioMap = emptyAspectRatioMap();
        if (contract.dam && contract.mimeType?.startsWith('image/')) {
            for (const key of Object.keys(AspectRatio)) {
                map[AspectRatio[key]] = await this.getUrlFromMediaContract(contract, { aspectRatio: AspectRatio[key]});
            }
        } else {
            throw new Error(`Aspect ratios cannot be defined on such media (${contract.key}). DAM media: ${contract.dam}, image mime type: ${contract.mimeType}.`);
        }
        return map;
    }
}